import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { LoadingSpinner, VideoButton, styles, Icon } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { PlayerReport, useOktaQuery, isLhb, useMultiVideoFn } from '../data';
import usePlayerSelector from '../utils/usePlayerSelector';
import { mapHittingMonitorScatterPlotSeries } from '../data/dataMapping';
import ReportCard from './ReportCard';
import StrikeZone from './StrikeZone';
import { VideoPlayerModal } from './VideoPlayer';

const StyledCol = styled(Col)`
    padding: 0 !important;
    @media (max-width: 1024px) {
        padding-left: 20px !important;
        padding-right: 0 !important;
    }
`;
const StyledRow = styled(Row)`
    justify-content: space-between;
`;
const StyledChartsContainer = styled.div`
    height: 100%;
`;
const StyledVideoButton = styled(VideoButton)`
    margin-left: 2em;
    > span {
        font-size: 10px;
    }
`;

const KeyTakeaway = ({ takeaway, showPlayerName, isOpen }) => {
    const { t: translate } = useTranslation();
    const [ktOpen, setKtOpen] = useState(isOpen);
    const [visibleSeriesIndices, setVisibleSeriesIndices] = useState(null);
    const [scatterPlotSeries, setScatterPlotSeries] = useState([]);
    const videoPlayerModalContainer = useRef();
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoPlaylist, setVideoPlaylist] = useState(null);
    const userPlayer = usePlayerSelector();
    const isLHB = takeaway.Filters?.bats ? (takeaway.Filters.bats === 2) : isLhb(userPlayer, takeaway.Player);
    const multiVideoFn = useMultiVideoFn();

    const hitmonQueryParams = {
        playerid: takeaway.PlayerID,
        takeawayid: takeaway.TakeawayID,
    };
    const { data: hitmonData, isFetching: loadingHitmonData } = useOktaQuery(
        ['HitmonKeyTakeaway', hitmonQueryParams],
        (t) => PlayerReport.hitmonPitches(t, hitmonQueryParams),
        { enabled: ktOpen },
    );

    useEffect(() => {
        seriesify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hitmonData]);

    function seriesify() {
        if (hitmonData && hitmonData.pitches) {
            setScatterPlotSeries(mapHittingMonitorScatterPlotSeries(translate, hitmonData.pitches, takeaway.DisplayAs === 1));
        }
    }

    const playAllVideo = async () => {
        if (window.$MatchupEmbeddedVideoDisabled) return;
        setShowVideoModal(true);
        let pitches = hitmonData?.pitches;
        if (visibleSeriesIndices != null) {
            pitches = pitches.filter((v) => {
                if (takeaway.DisplayAs === 0) {
                    return visibleSeriesIndices.some((i) => i === v.hitmonresulttype);
                }
                return visibleSeriesIndices.some((i) => i === v.pitchtype);
            });
        }
        const playlist = await multiVideoFn(pitches);
        setVideoPlaylist(playlist);
    };

    const legendClickFn = (series) => {
        setVisibleSeriesIndices(series?.filter((s) => s.visible).map((s) => s.userOptions.index));
    };

    return (
        <>
            <ReportCard>
                <h4>
                    {showPlayerName && `${takeaway.Player.FullName} - `}{takeaway.Name}
                    {!window.$IsDugoutApp && scatterPlotSeries && !loadingHitmonData && (
                        <StyledVideoButton icon={translate('common.all')} onClick={playAllVideo} color={styles.theme.background.astronaut} size="2em">
                            {translate('components.key-takeaway.play-all')}
                        </StyledVideoButton>
                    )}
                    <span className="small" style={{ float: 'right', cursor: 'pointer' }} onClick={() => setKtOpen(!ktOpen)}>
                        {ktOpen ? <Icon icon="chevron-up" /> : <Icon icon="chevron-down" />}
                    </span>
                </h4>
                {ktOpen && (
                    <StyledRow style={{ margin: '0' }}>
                        <StyledCol md={4}>
                            { /* eslint-disable-next-line react/no-danger */ }
                            <h5 dangerouslySetInnerHTML={{ __html: takeaway.Description.replace(/\n/g, '<br />').replace(/\\n/g, '<br />') }} />
                        </StyledCol>
                        <StyledCol md={8}>
                            {loadingHitmonData && <LoadingSpinner />}
                            {!loadingHitmonData && (
                                <StyledChartsContainer>
                                    <div className="flex-grow-1 d-flex align-items-center">
                                        { scatterPlotSeries
                                            ? (
                                                <StrikeZone
                                                    showlegend
                                                    showlhb={isLHB}
                                                    legendposition="below"
                                                    seriesData={scatterPlotSeries}
                                                    height={435}
                                                    legendClickFn={legendClickFn}
                                                />
                                            ) : <><br />&nbsp;&nbsp;&nbsp;{translate('components.key-takeaway.no-pitches-found')}</>}
                                    </div>
                                </StyledChartsContainer>
                            )}
                        </StyledCol>
                    </StyledRow>
                )}
            </ReportCard>
            <div ref={videoPlayerModalContainer} />
            <VideoPlayerModal
                playlist={videoPlaylist}
                show={showVideoModal}
                onHide={() => { setVideoPlaylist(null); setShowVideoModal(false); }}
                container={videoPlayerModalContainer}
            />
        </>
    );
};

KeyTakeaway.propTypes = {
    takeaway: PropTypes.shape({
        TakeawayID: PropTypes.number,
        PlayerID: PropTypes.number,
        Name: PropTypes.string,
        Description: PropTypes.string,
        YearID: PropTypes.number,
        StartDate: PropTypes.string,
        EndDate: PropTypes.string,
        OppOrgID: PropTypes.number,
        LevelID: PropTypes.number,
        Player: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
        DisplayAs: PropTypes.number,
        Filters: PropTypes.shape({
            bats: PropTypes.number,
        }),
    }).isRequired,
    showPlayerName: PropTypes.bool,
    isOpen: PropTypes.bool,
};

KeyTakeaway.defaultProps = {
    showPlayerName: false,
    isOpen: true,
};

export default KeyTakeaway;
